/**
 * FormatService
 * Provides locale-specific formatting for numbers, dates, currencies, etc.
 * Uses native Intl APIs for zero-dependency formatting
 * Implements IFormatService interface for dependency injection
 */

import type { IFormatService, NumberFormatOptions, DateFormatOptions } from '../../types/i18n';
import { DEFAULT_LANGUAGE } from '../../config/i18n.config';

export class FormatService implements IFormatService {
  // Cache formatters for performance
  private numberFormatters: Map<string, Intl.NumberFormat> = new Map();
  private dateFormatters: Map<string, Intl.DateTimeFormat> = new Map();

  /**
   * Format a number according to locale
   * @param value Number to format
   * @param locale Locale code
   * @param options Formatting options
   * @returns Formatted number string
   */
  formatNumber(value: number, locale: string, options?: NumberFormatOptions): string {
    try {
      // Handle special values
      if (Number.isNaN(value)) {
        return '—';
      }
      if (!Number.isFinite(value)) {
        return value > 0 ? '∞' : '-∞';
      }

      const cacheKey = this.getNumberFormatterCacheKey(locale, options);
      let formatter = this.numberFormatters.get(cacheKey);

      if (!formatter) {
        const intlOptions: Intl.NumberFormatOptions = {
          style: options?.style || 'decimal',
        };

        if (options?.decimals !== undefined) {
          intlOptions.minimumFractionDigits = options.decimals;
          intlOptions.maximumFractionDigits = options.decimals;
        }

        if (options?.currency) {
          intlOptions.currency = options.currency;
        }

        if (options?.compact) {
          intlOptions.notation = 'compact';
        }

        formatter = new Intl.NumberFormat(locale || DEFAULT_LANGUAGE, intlOptions);
        this.numberFormatters.set(cacheKey, formatter);
      }

      return formatter.format(value);
    } catch (error) {
      console.error('[FormatService] Error formatting number:', error);
      return String(value);
    }
  }

  /**
   * Format a date according to locale
   * @param date Date to format
   * @param locale Locale code
   * @param options Formatting options
   * @returns Formatted date string
   */
  formatDate(date: Date, locale: string, options?: DateFormatOptions): string {
    try {
      // Handle invalid dates
      if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return '—';
      }

      const cacheKey = this.getDateFormatterCacheKey(locale, options);
      let formatter = this.dateFormatters.get(cacheKey);

      if (!formatter) {
        const intlOptions: Intl.DateTimeFormatOptions = {};

        // Date style
        if (options?.format) {
          intlOptions.dateStyle = options.format;
        } else {
          intlOptions.dateStyle = 'medium';
        }

        // Time options
        if (options?.includeTime) {
          intlOptions.timeStyle = 'short';
          if (options?.includeSeconds) {
            intlOptions.timeStyle = 'medium';
          }
        }

        // Timezone (default to UTC for consistency)
        intlOptions.timeZone = options?.timeZone || 'UTC';

        formatter = new Intl.DateTimeFormat(locale || DEFAULT_LANGUAGE, intlOptions);
        this.dateFormatters.set(cacheKey, formatter);
      }

      return formatter.format(date);
    } catch (error) {
      console.error('[FormatService] Error formatting date:', error);
      return date.toLocaleDateString();
    }
  }

  /**
   * Format currency amount
   * @param amount Amount to format
   * @param currency Currency code (e.g., 'USD', 'EUR')
   * @param locale Locale code
   * @returns Formatted currency string
   */
  formatCurrency(amount: number, currency: string, locale: string): string {
    try {
      // Handle special values
      if (Number.isNaN(amount)) {
        return '—';
      }
      if (!Number.isFinite(amount)) {
        return amount > 0 ? '∞' : '-∞';
      }

      return this.formatNumber(amount, locale, {
        style: 'currency',
        currency: currency || 'USD',
      });
    } catch (error) {
      console.error('[FormatService] Error formatting currency:', error);
      return `${currency} ${amount.toFixed(2)}`;
    }
  }

  /**
   * Format time (hours:minutes with locale-specific format)
   * @param date Date object to extract time from
   * @param locale Locale code
   * @param includeSeconds Whether to include seconds
   * @returns Formatted time string
   */
  formatTime(date: Date, locale: string, includeSeconds?: boolean): string {
    try {
      // Handle invalid dates
      if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return '—';
      }

      const options: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
      };

      if (includeSeconds) {
        options.second = '2-digit';
      }

      const formatter = new Intl.DateTimeFormat(locale || DEFAULT_LANGUAGE, options);
      return formatter.format(date);
    } catch (error) {
      console.error('[FormatService] Error formatting time:', error);
      return date.toLocaleTimeString();
    }
  }

  /**
   * Format percentage
   * @param value Value to format (0.5 = 50%)
   * @param locale Locale code
   * @param decimals Number of decimal places
   * @returns Formatted percentage string
   */
  formatPercentage(value: number, locale: string, decimals: number = 0): string {
    try {
      // Handle special values
      if (Number.isNaN(value)) {
        return '—';
      }
      if (!Number.isFinite(value)) {
        return value > 0 ? '∞' : '-∞';
      }

      return this.formatNumber(value, locale, {
        style: 'percent',
        decimals,
      });
    } catch (error) {
      console.error('[FormatService] Error formatting percentage:', error);
      return `${(value * 100).toFixed(decimals)}%`;
    }
  }

  /**
   * Format file size in bytes to human-readable format
   * @param bytes Number of bytes
   * @param locale Locale code
   * @param binary Use binary (1024) or decimal (1000) units
   * @returns Formatted file size string
   */
  formatFileSize(bytes: number, locale: string, binary: boolean = true): string {
    try {
      // Handle special values
      if (Number.isNaN(bytes) || bytes < 0) {
        return '—';
      }
      if (!Number.isFinite(bytes)) {
        return '∞';
      }

      // Handle zero
      if (bytes === 0) {
        return '0 B';
      }

      const base = binary ? 1024 : 1000;
      const units = binary
        ? ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB']
        : ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];

      const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(base)), units.length - 1);
      const value = bytes / Math.pow(base, exponent);

      // Format with appropriate decimal places
      const decimals = exponent === 0 ? 0 : 2;
      const formattedValue = this.formatNumber(value, locale, { decimals });

      return `${formattedValue} ${units[exponent]}`;
    } catch (error) {
      console.error('[FormatService] Error formatting file size:', error);
      return `${bytes} B`;
    }
  }

  /**
   * Generate cache key for number formatters
   * @private
   */
  private getNumberFormatterCacheKey(locale: string, options?: NumberFormatOptions): string {
    const parts = [locale || DEFAULT_LANGUAGE];

    if (options) {
      if (options.style) parts.push(options.style);
      if (options.decimals !== undefined) parts.push(`d${options.decimals}`);
      if (options.currency) parts.push(options.currency);
      if (options.compact) parts.push('compact');
    }

    return parts.join('|');
  }

  /**
   * Generate cache key for date formatters
   * @private
   */
  private getDateFormatterCacheKey(locale: string, options?: DateFormatOptions): string {
    const parts = [locale || DEFAULT_LANGUAGE];

    if (options) {
      if (options.format) parts.push(options.format);
      if (options.includeTime) parts.push('time');
      if (options.includeSeconds) parts.push('seconds');
      if (options.timeZone) parts.push(options.timeZone);
    }

    return parts.join('|');
  }
}

// Export singleton instance for convenience (but allow injection for testing)
export const formatService = new FormatService();
